import React, { useCallback, useMemo } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import CheckboxInputListItem from 'LuxKit/List/CheckboxInputListItem'
import useExperiencesSearchFilters from 'components/Experiences/hooks/useExperiencesSearchFilters'
import useTrackExperienceSearchFilter from 'components/Experiences/hooks/useTrackExperienceSearchFilter'
import FilterPanelCheckboxGroup, {
  type FilterPanelCheckItem,
} from 'components/Common/FilterPanel/FilterPanelCheckboxGroup'

const ListContainer = styled.div`
  margin-inline: ${rem(-16)};
  margin-bottom: ${rem(-8)};
`

const messages = defineMessages({
  freeCancellation: {
    id: 'gnc42bYCUYEC1VtZ_FBb',
    defaultMessage: 'Free cancellation',
    description: 'Form field label for free cancellation policy on experience',
  },
  instantConfirmation: {
    id: 'liiEI8aOkzbI4FNe-4v4',
    defaultMessage: 'Instant confirmation',
    description:
      'Featured filter option for instant confirmation in experience search',
  },
  isGift: {
    id: 'edMa9w6iG60jFtGtkRgD',
    defaultMessage: 'Gift inspiration',
    description:
      'Featured filter option for gift inspiration in experience search',
  },
})

const FEATURED_OPTIONS: Array<{
  value: Experiences.FeaturedFilter;
  messageKey: keyof typeof messages;
}> = [
  { value: 'free-cancellation', messageKey: 'freeCancellation' },
  { value: 'instant-confirmation', messageKey: 'instantConfirmation' },
  { value: 'is-gift', messageKey: 'isGift' },
]

interface Props {
  featuredCounts?: Record<Experiences.FeaturedFilter, number>;
  compact?: boolean;
  hideCounts?: boolean;
}

function ExperienceSearchFeaturedFilter({ featuredCounts, compact = false, hideCounts = false }: Props) {
  const intl = useIntl()
  const { searchFilters, setSearchFilters } = useExperiencesSearchFilters()
  const trackSearchFilter = useTrackExperienceSearchFilter()

  const selectedFeatured = useMemo(
    () => searchFilters.featured ?? [],
    [searchFilters.featured],
  )

  const onToggle = useCallback(
    (value: Experiences.FeaturedFilter) => {
      const option = FEATURED_OPTIONS.find(option => option.value === value)
      const selected = selectedFeatured.includes(value)
      if (option) {
        trackSearchFilter({
          label: intl.formatMessage(messages[option.messageKey]),
          type: 'featured',
          value: selected ? 'unselected' : 'selected',
        })
      }
      const next = selectedFeatured.includes(value)
        ? selectedFeatured.filter((v) => v !== value)
        : [...selectedFeatured, value]
      setSearchFilters({
        ...searchFilters,
        featured: next.length > 0 ? next : undefined,
      })
    },
    [intl, selectedFeatured, searchFilters, setSearchFilters, trackSearchFilter],
  )

  const compactItems = useMemo<Array<FilterPanelCheckItem>>(() => {
    return FEATURED_OPTIONS.map((option) => ({
      value: option.value,
      label: intl.formatMessage(messages[option.messageKey]),
      checked: selectedFeatured.includes(option.value),
      count: featuredCounts?.[option.value],
      hideCount: hideCounts,
      disabled: featuredCounts?.[option.value] === 0,
    }))
  }, [featuredCounts, hideCounts, intl, selectedFeatured])

  const onCompactToggle = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    onToggle(event.target.value as Experiences.FeaturedFilter)
  }, [onToggle])

  if (compact) {
    return (
      <FilterPanelCheckboxGroup
        name="featured"
        items={compactItems}
        onChange={onCompactToggle}
      />
    )
  }

  return (
    <ListContainer>
      {FEATURED_OPTIONS.map((option) => (
        <CheckboxInputListItem
          key={option.value}
          inputPlacement="start"
          checked={selectedFeatured.includes(option.value)}
          onChange={() => onToggle(option.value)}
          title={intl.formatMessage(messages[option.messageKey])}
          size="medium"
          disabled={featuredCounts?.[option.value] === 0}
        />
      ))}
    </ListContainer>
  )
}

export default ExperienceSearchFeaturedFilter
